import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import withForm from 'modules/common/hoc/withForm';
import Modal from 'modules/common/components/Modal';
import useFormValidator from 'useFormValidator';
import moment from 'moment';
import PropTypes from 'prop-types';
import { ModalContext } from 'App';
import * as actions from '../../action';
import * as c from '../../constant';
import LandReference from './components/LandReference';
import GeneralDescription from './components/GeneralDescription';
import OwnerDetails from './components/OwnerDetails';
import AdministratorDetails from './components/AdministratorDetails';

function MachineryApplicationModal({
  createRichTextEditor,
  updateMachineryApplication,
  formData,
  setFormData,
  id,
  getMachineryApplicationsList,
  getSingleMachineryApplication,
  applicationInfo,
  htmlToDraft,
  draftToHtml,
  convertToRaw,
}) {
  const setModal = useContext(ModalContext);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const RULES = {
    page0: {
      rules: {
        rptax_machinery_owner_id: 'required',
      },
    },
    page1: {
      rules: {},
    },
    page2: {
      rules: {
        tdn: 'required|rptax_tdn',
        land_application_id: 'required',
        property_district_id: 'required',
        property_barangay_id: 'required',
        property_street: 'required',
      },
    },
    page3: {
      rules: {
        effectivity_of_assessment_reassessment_yr: 'required',
        effectivity_of_assessment_reassessment_qtr: 'required',
        appraised_assessed_by_id: 'required',
        appraised_assessed_at: 'required',
        recommending_by_id: 'required',
        recommending_at: 'required',
        approved_by_id: 'required',
        approved_at: 'required',
        registered: 'required',
      },
    },
  };
  const page0Validator = useFormValidator(formData, RULES.page0.rules);
  const page1Validator = useFormValidator(formData, RULES.page1.rules);
  const page2Validator = useFormValidator(formData, RULES.page2.rules);
  const page3Validator = useFormValidator(formData, RULES.page3.rules);

  const formatDateTimeForForm = (value) => {
    if (!value) {
      return '';
    }
    const date = moment(value);
    return date.isValid() ? date.format('MM/DD/YYYY HH:mm') : '';
  };

  const validateForm = () => {
    if (selectedIndex === 0) {
      if (!page0Validator.response.is_passed) {
        return false;
      }
      return setSelectedIndex(+selectedIndex + 1);
    }
    if (selectedIndex === 1) {
      if (!page1Validator.response.is_passed) {
        return false;
      }
      return setSelectedIndex(+selectedIndex + 1);
    }
    if (selectedIndex === 2) {
      if (!page2Validator.response.is_passed) {
        return false;
      }
      return setSelectedIndex(+selectedIndex + 1);
    }
    if (selectedIndex === 3) {
      if (!page3Validator.response.is_passed) {
        return false;
      }
    }
    return true;
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    if (validateForm() !== true) {
      return false;
    }

    const rptax_machinery_co_owner_ids = [];

    (_.get(formData, 'co_owners_ids') || []).forEach((info) => {
      rptax_machinery_co_owner_ids.push(info.co_owners_ids);
    });

    const args = _.pick(
      {
        ...formData,
        registered: `${moment(_.get(formData, 'registered')).format(
          'YYYY-MM-DD HH:mm'
        )}:00`,
        appraised_assessed_at: `${moment(
          _.get(formData, 'appraised_assessed_at')
        ).format('YYYY-MM-DD HH:mm')}:00`,
        recommending_at: `${moment(_.get(formData, 'recommending_at')).format(
          'YYYY-MM-DD HH:mm'
        )}:00`,
        approved_at: `${moment(_.get(formData, 'approved_at')).format(
          'YYYY-MM-DD HH:mm'
        )}:00`,
        memoranda: draftToHtml(
          convertToRaw(formData.memoranda.getCurrentContent())
        ),
        rptax_machinery_co_owner_ids
      },
      _.keys(c.EDIT_MACHINERY_APPLICATION_FORM_DATA)
    );
    if (selectedIndex === 3) {
      updateMachineryApplication(id, args, ({ data }) => {
        setModal({ isOpen: false });
        getMachineryApplicationsList();
        getSingleMachineryApplication(data.id);
      });
    }
    return false;
  };

  useEffect(() => {
    if (!_.isEmpty(applicationInfo)) {
      const co_owners_ids = [];
      (
        _.get(applicationInfo, 'included.machineryApplicationCoOwners') || []).forEach((info) => {
          co_owners_ids.push({
            id: _.get(info, 'id'),
            co_owners_ids: _.get(info, 'id'),
          });
        });

      const args = {
        ...applicationInfo?.attributes,
        rptax_transaction_code_id:
          applicationInfo?.attributes?.rptax_transaction_code_id !== null &&
            applicationInfo?.attributes?.rptax_transaction_code_id !== ''
            ? `${applicationInfo?.attributes?.rptax_transaction_code_id}`
            : '',
        transaction_code:
          applicationInfo?.included?.transactionCode?.attributes.code ?? '',
        rptax_machinery_owner_id:
          applicationInfo?.attributes?.rptax_machinery_owner_id !== null &&
            applicationInfo?.attributes?.rptax_machinery_owner_id !== ''
            ? `${applicationInfo?.attributes?.rptax_machinery_owner_id}`
            : '',
        machinery_owner_telephone_no:
          applicationInfo?.included?.machineryOwner?.attributes?.telephone_no,
        machinery_owner_tin:
          applicationInfo?.included?.machineryOwner?.attributes?.tin,
        machinery_owner_region_code:
          applicationInfo?.included?.machineryOwner?.attributes?.region_name,
        machinery_owner_province_code:
          applicationInfo?.included?.machineryOwner?.attributes?.province_name,
        machinery_owner_municipality_code:
          applicationInfo?.included?.machineryOwner?.attributes
            ?.municipality_name,
        machinery_owner_barangay_code:
          applicationInfo?.included?.machineryOwner?.attributes?.barangay_name,
        machinery_owner_address_line:
          applicationInfo?.included?.machineryOwner?.attributes?.address_line,
        beneficial_user_id:
          applicationInfo?.attributes?.beneficial_user_id !== null &&
            applicationInfo?.attributes?.beneficial_user_id !== ''
            ? `${applicationInfo?.attributes?.beneficial_user_id}`
            : '',
        beneficial_user_telephone_no:
          applicationInfo?.included?.beneficialUser?.attributes?.telephone_no,
        beneficial_user_tin:
          applicationInfo?.included?.beneficialUser?.attributes?.tin,
        beneficial_user_region_code:
          applicationInfo?.included?.beneficialUser?.attributes?.region_name,
        beneficial_user_province_code:
          applicationInfo?.included?.beneficialUser?.attributes?.province_name,
        beneficial_user_municipality_code:
          applicationInfo?.included?.beneficialUser?.attributes
            ?.municipality_name,
        beneficial_user_barangay_code:
          applicationInfo?.included?.beneficialUser?.attributes?.barangay_name,
        beneficial_user_address_line:
          applicationInfo?.included?.beneficialUser?.attributes?.address_line,
        machine_location:
          applicationInfo?.attributes?.rptax_building_owner_id !== null
            ? 'BUILDING'
            : 'LAND',
        memoranda: htmlToDraft(applicationInfo?.attributes?.memoranda),
        appraised_assessed_at: formatDateTimeForForm(
          _.get(applicationInfo?.attributes, 'appraised_assessed_at')
        ),
        recommending_at: formatDateTimeForForm(
          _.get(applicationInfo?.attributes, 'recommending_at')
        ),
        approved_at: formatDateTimeForForm(
          _.get(applicationInfo?.attributes, 'approved_at')
        ),
        registered: formatDateTimeForForm(
          _.get(applicationInfo?.attributes, 'registered')
        ),
        co_owners_ids,
      };
      if (
        applicationInfo?.attributes?.building_application_id !== null &&
        applicationInfo?.attributes?.building_application_id !== ''
      ) {
        args.tdn =
          applicationInfo?.included?.buildingApplication?.attributes?.tdn;
        args.building_owner_name =
          applicationInfo?.included?.buildingOwner?.attributes?.full_name;
        args.building_application_pin =
          applicationInfo?.included?.buildingApplication?.attributes?.pin;
        args.building_application_id =
          applicationInfo?.included?.buildingApplication?.id;
        args.property_district_id =
          applicationInfo?.included?.buildingApplication?.attributes?.property_district_id;
        args.property_barangay_id =
          applicationInfo?.included?.buildingApplication?.attributes?.property_barangay_id;
        args.property_street =
          applicationInfo?.included?.buildingApplication?.attributes?.property_street;
      }
      if (
        applicationInfo?.attributes?.building_application_id === null ||
        applicationInfo?.attributes?.building_application_id === ''
      ) {
        args.tdn = applicationInfo?.included?.landApplication?.attributes?.tdn;
        args.land_owner_name =
          applicationInfo?.included?.landOwner?.attributes?.name;
        args.land_owner_pin =
          applicationInfo?.included?.landApplication?.attributes?.pin;
        args.property_district_id =
          applicationInfo?.included?.landApplication?.attributes?.property_district_id;
        args.property_barangay_id =
          applicationInfo?.included?.landApplication?.attributes?.property_barangay_id;
        args.property_street =
          applicationInfo?.included?.landApplication?.attributes?.property_street;
      }
      setFormData(args);
    }
  }, [applicationInfo]);

  useEffect(() => {
    if (formData?.machine_location === 'BUILDING') {
      page1Validator.onSetObjectRules({
        ...RULES.page1.rules,
        rptax_building_owner_id: 'required',
      });
    }
    page1Validator.onSetObjectRules(RULES.page1.rules);
  }, [formData?.machine_location]);

  return (
    <Modal.Body>
      <form onSubmit={handleOnSubmit}>
        {selectedIndex === 0 && (
          <OwnerDetails
            formData={formData}
            setFormData={setFormData}
            validateForm={page0Validator}
          />
        )}
        {selectedIndex === 1 && (
          <AdministratorDetails
            formData={formData}
            setFormData={setFormData}
            setSelectedIndex={setSelectedIndex}
            validateForm={page1Validator}
          />
        )}
        {selectedIndex === 2 && (
          <LandReference
            formData={formData}
            setFormData={setFormData}
            setSelectedIndex={setSelectedIndex}
            validateForm={page2Validator}
          />
        )}
        {selectedIndex === 3 && (
          <GeneralDescription
            formData={formData}
            setFormData={setFormData}
            setSelectedIndex={setSelectedIndex}
            validateForm={page3Validator}
            createRichTextEditor={createRichTextEditor}
            applicationId={id}
          />
        )}
      </form>
    </Modal.Body>
  );
}

MachineryApplicationModal.defaultProps = {
  id: '',
  applicationInfo: {},
};

MachineryApplicationModal.propTypes = {
  createRichTextEditor: PropTypes.instanceOf(Function).isRequired,
  updateMachineryApplication: PropTypes.instanceOf(Function).isRequired,
  formData: PropTypes.instanceOf(Object).isRequired,
  setFormData: PropTypes.instanceOf(Function).isRequired,
  getMachineryApplicationsList: PropTypes.instanceOf(Function).isRequired,
  getSingleMachineryApplication: PropTypes.instanceOf(Function).isRequired,
  draftToHtml: PropTypes.instanceOf(Function).isRequired,
  convertToRaw: PropTypes.instanceOf(Function).isRequired,
  id: PropTypes.string,
  applicationInfo: PropTypes.instanceOf(Object),
  htmlToDraft: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  transactionList: _.get(api, `${c.GET_TRANSACTION_CODES}.list`) || [],
  landOwnerList: _.get(api, `${c.GET_LAND_OWNER}.list`) || [],
  machineryOwnerList: _.get(api, `${c.GET_MACHINERY_OWNER}.list`) || [],
  buildingOwnerList: _.get(api, `${c.GET_BUILDING_OWNER}.list`) || [],
  provinceList: _.get(api, `${c.GET_PROVINCE}.list`) || [],
  municipalityList: _.get(api, `${c.GET_MUNICIPALITIES}.list`) || [],
  barangayList: _.get(api, `${c.GET_BARANGAYS}.list`) || [],
});

const enhance = _.flowRight([
  withForm,
  withRouter,
  connect(mapStateToProps, {
    ...actions,
  }),
]);

export default enhance(MachineryApplicationModal);
